/**
 * Efficiency, from the learner's side (docs/efficiency-plan.md, Phases 3 and
 * 5): the client for the meter and the study, the addresses of the two
 * pages, and how a micro-dollar figure is written down.
 *
 * The shapes are the engine's (packages/engine/src/efficiency.ts) and every
 * answer is checked here the way lib/projects.ts checks its board: the Worker
 * may be a deploy ahead of or behind this bundle, and a figure that does not
 * read is left out rather than drawn. A cost the browser made up is a claim
 * about what somebody's agent spent.
 *
 * Nothing here ever carries prompt or answer text, because the meter never
 * stores any. The study's arm is never read either: the Worker sends only
 * `shown`, and a page that cannot see the arm cannot name it.
 */
import {
  BENCH_FORMS,
  TECHNIQUES,
  type Attempt,
  type AttemptFeedback,
  type AttemptKind,
  type AttemptOutcome,
  type BenchForm,
  type BenchTaskState,
  type EfficiencySummary,
  type MeterAnswer,
  type ParTotals,
  type RunUsage,
  type StudyStage,
  type StudyStatus,
  type Technique,
} from '@engineer-os/engine';
import { chatUrl } from './chat';
import { intlTag } from './format';
import type { Locale } from './locale';
import type { StudyRefusal } from './strings/efficiency';

/* ── Addresses ─────────────────────────────────────────────────────────── */

/** The study's page. Not linked from the nav yet: it opens with the cohorts. */
export const STUDY_PATH = '/study';
/** The techniques, one anchor each, linked from the cost panel after a pass. */
export const TECHNIQUES_PATH = '/efficiency';

export function isStudyPath(pathname: string): boolean {
  return pathname === STUDY_PATH || pathname === `${STUDY_PATH}/`;
}

export function isTechniquesPath(pathname: string): boolean {
  return pathname === TECHNIQUES_PATH || pathname === `${TECHNIQUES_PATH}/`;
}

export function techniqueHref(technique: Technique): string {
  return `${TECHNIQUES_PATH}#${technique}`;
}

/**
 * The consent text's edition, sent with the enrolment and stored beside it.
 * The Worker refuses any other (`CONSENT_VERSION` in worker/study.ts), and
 * the words on /study are that edition's (docs/efficiency-study.md,
 * "Informed consent"): a changed text is a new version, in both places.
 */
export const STUDY_CONSENT_VERSION = 'study-2026-10-v1';

/* ── Writing money down ────────────────────────────────────────────────── */

/** Below half a cent a figure rounds to $0.00, which reads as free; it is said as "under a cent" instead. */
const HALF_CENT_MICRO = 5_000;

/**
 * A micro-dollar amount in dollars and cents, always two places ("$0.40",
 * never "$0.4" or "$1"), or null when it is under half a cent and the caller
 * should say "under $0.01" in its own words. Zero is "$0.00": nothing was
 * spent, and that is a figure.
 */
export function usdFromMicro(micro: number, locale?: Locale): string | null {
  if (micro > 0 && micro < HALF_CENT_MICRO) return null;
  return usd(Math.round(micro / 10_000) / 100, locale);
}

/** One cent, written in the reader's language, for "under $0.01". */
export function oneCent(locale?: Locale): string {
  return usd(0.01, locale);
}

function usd(dollars: number, locale?: Locale): string {
  return new Intl.NumberFormat(intlTag(locale), {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

/** A ratio to par, one decimal place ("0.7", "1,4" in German). */
export function ratioText(ratio: number, locale?: Locale): string {
  return new Intl.NumberFormat(intlTag(locale), { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(ratio);
}

/* ── Reading the meter ─────────────────────────────────────────────────── */

const KINDS: readonly AttemptKind[] = ['level', 'checkpoint', 'task', 'bench'];
const OUTCOMES: readonly AttemptOutcome[] = ['open', 'passed', 'failed', 'abandoned'];

const count = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v >= 0;
const stamp = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v) && v > 0;

export function readAttempt(raw: unknown): Attempt | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  if (typeof a.id !== 'string' || !a.id || typeof a.ref !== 'string' || !a.ref) return null;
  if (!KINDS.includes(a.kind as AttemptKind) || !OUTCOMES.includes(a.outcome as AttemptOutcome)) return null;
  if (!stamp(a.startedAt) || !count(a.costMicro) || !count(a.calls)) return null;
  return {
    id: a.id,
    kind: a.kind as AttemptKind,
    ref: a.ref,
    startedAt: a.startedAt,
    endedAt: stamp(a.endedAt) ? a.endedAt : null,
    outcome: a.outcome as AttemptOutcome,
    book: typeof a.book === 'string' ? a.book : '',
    // Anything but a plain "agent" with calls behind it is a hand attempt,
    // which has no cost to show: an agent figure the ledger cannot back is
    // the one thing this page must not draw.
    by: a.by === 'agent' && a.calls > 0 ? 'agent' : 'hand',
    calls: Math.trunc(a.calls),
    costMicro: Math.round(a.costMicro),
    // A ratio only beside a pass by an agent, as with feedback's.
    ratio: a.outcome === 'passed' && a.by === 'agent' && count(a.ratio) && a.ratio > 0 ? a.ratio : null,
    // Counted from the learner's own tool's reports rather than at our gateway.
    reported: a.reported === true,
  };
}

export function readFeedback(raw: unknown): AttemptFeedback | null {
  if (!raw || typeof raw !== 'object') return null;
  const f = raw as Record<string, unknown>;
  const attempt = readAttempt(f.attempt);
  if (!attempt) return null;
  const parMicro = count(f.parMicro) && f.parMicro > 0 ? f.parMicro : null;
  // The ratio is drawn only beside the par it came from, and only for a pass.
  const ratio = parMicro !== null && attempt.outcome === 'passed' && count(f.ratio) ? f.ratio : null;
  const costliest: AttemptFeedback['costliest'] = [];
  if (Array.isArray(f.costliest)) {
    for (const c of f.costliest.slice(0, 3)) {
      if (!c || typeof c !== 'object') continue;
      const call = c as Record<string, unknown>;
      if (typeof call.model !== 'string' || !call.model || call.model.length > 120) continue;
      if (!count(call.inputTokens) || !count(call.outputTokens) || !count(call.bookCostMicro)) continue;
      costliest.push({
        at: stamp(call.at) ? call.at : 0,
        model: call.model,
        inputTokens: Math.trunc(call.inputTokens),
        outputTokens: Math.trunc(call.outputTokens),
        bookCostMicro: Math.round(call.bookCostMicro),
      });
    }
  }
  const technique = TECHNIQUES.includes(f.technique as Technique) ? (f.technique as Technique) : null;
  // The model par was measured on, only beside the par it belongs to, and only
  // when it reads as a model id: it is drawn as advice to try that model.
  const parModel = parMicro !== null && typeof f.parModel === 'string' && MODEL_ID.test(f.parModel) ? f.parModel : null;
  return { attempt, parMicro, ratio, costliest, technique, parModel, practice: readPractice(f.practice, attempt), usage: readUsage(f.usage) };
}

/** One passing run, as the two-runs comparison reads it. */
export interface RunFigures {
  costMicro: number;
  usage: RunUsage;
}

/**
 * What changed between the first run and the latest (TwoRuns.tsx): the ratio
 * of tokens used, the ratio of the average price of a token, and whether the
 * latest looks like an agent that carried on an earlier session instead of
 * starting fresh. Their product is the cost ratio, so the two halves say what
 * made the saving: less work, a cheaper model, or both.
 */
export function runDifference(first: RunFigures, latest: RunFigures): { tokenRatio: number; priceRatio: number; carriedOver: boolean } | null {
  const tokens = (u: RunUsage) => u.inputTokens + u.outputTokens;
  const t1 = tokens(first.usage), t2 = tokens(latest.usage);
  if (t1 <= 0 || t2 <= 0 || first.costMicro <= 0 || latest.costMicro <= 0) return null;
  const tokenRatio = t2 / t1;
  const priceRatio = (latest.costMicro / t2) / (first.costMicro / t1);
  // A fresh agent opens on its own instructions; one that kept the earlier
  // conversation opens far larger. Generous margins: only a clear case says so.
  const f1 = first.usage.firstInputTokens, f2 = latest.usage.firstInputTokens;
  const carriedOver = f1 > 0 && f2 > Math.max(1.5 * f1, f1 + 20_000);
  return { tokenRatio, priceRatio, carriedOver };
}

/** A run's token totals and cost by model, or null when any figure does not read. */
export function readUsage(raw: unknown): RunUsage | null {
  if (!raw || typeof raw !== 'object') return null;
  const u = raw as Record<string, unknown>;
  const tokens = ['inputTokens', 'cachedTokens', 'cacheWriteTokens', 'outputTokens', 'firstInputTokens'] as const;
  if (!tokens.every((k) => count(u[k]))) return null;
  if (!Array.isArray(u.models) || u.models.length === 0) return null;
  const models: RunUsage['models'] = [];
  for (const m of u.models.slice(0, 6)) {
    if (!m || typeof m !== 'object') return null;
    const row = m as Record<string, unknown>;
    if (typeof row.model !== 'string' || !row.model || row.model.length > 120) return null;
    if (!count(row.calls) || row.calls < 1 || !count(row.costMicro)) return null;
    models.push({ model: row.model, calls: Math.trunc(row.calls), costMicro: Math.round(row.costMicro) });
  }
  return {
    inputTokens: Math.trunc(u.inputTokens as number),
    cachedTokens: Math.trunc(u.cachedTokens as number),
    cacheWriteTokens: Math.trunc(u.cacheWriteTokens as number),
    outputTokens: Math.trunc(u.outputTokens as number),
    firstInputTokens: Math.trunc(u.firstInputTokens as number),
    models,
  };
}

/** Validate the optional private comparison before drawing money or a change. */
function readPractice(raw: unknown, attempt: Attempt): import('@engineer-os/engine').PracticeComparison | null {
  if (!raw || typeof raw !== 'object' || attempt.kind !== 'level' || attempt.outcome !== 'passed' || attempt.by !== 'agent') return null;
  const p = raw as Record<string, unknown>;
  if (p.book !== attempt.book || p.reported !== Boolean(attempt.reported)) return null;
  const spend = (raw: unknown): import('@engineer-os/engine').PracticeSpend | null => {
    if (!raw || typeof raw !== 'object') return null;
    const s = raw as Record<string, unknown>;
    if (!Number.isSafeInteger(s.costMicro) || (s.costMicro as number) < 0 || !Number.isSafeInteger(s.calls) || (s.calls as number) < 1 || !Number.isSafeInteger(s.attempts) || (s.attempts as number) < 1 || typeof s.passId !== 'string' || !s.passId) return null;
    return { costMicro: s.costMicro as number, calls: s.calls as number, attempts: s.attempts as number, passId: s.passId };
  };
  const before = spend(p.before);
  const after = p.after === null ? null : spend(p.after);
  if (!before || before.costMicro <= 0 || (p.after !== null && !after)) return null;
  if ((after ?? before).passId !== attempt.id || (after && after.passId === before.passId)) return null;
  if ((after ?? before).costMicro < attempt.costMicro || (after ?? before).calls < attempt.calls) return null;
  return { book: attempt.book, reported: Boolean(p.reported), before, after };
}

/** A model id as the gateway names one (`anthropic/claude-haiku-5.5`, `~anthropic/claude-haiku-latest`). */
const MODEL_ID = /^~?[a-z0-9][a-z0-9._-]*\/[a-z0-9][a-z0-9._:-]{0,100}$/i;

/** The learner's own passes against par, or null: three whole counts, a pass at least, par above zero. */
function readParTotals(raw: unknown): ParTotals | null {
  if (!raw || typeof raw !== 'object') return null;
  const t = raw as Record<string, unknown>;
  if (!count(t.spentMicro) || !count(t.parMicro) || t.parMicro <= 0 || !count(t.passes) || t.passes < 1) return null;
  return { spentMicro: Math.round(t.spentMicro), parMicro: Math.round(t.parMicro), passes: Math.trunc(t.passes) };
}

export function readMeter(raw: unknown): MeterAnswer | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  return {
    attempts: Array.isArray(m.attempts) ? m.attempts.slice(0, 50).map(readAttempt).filter(isPresent) : [],
    latest: m.shown === true ? readFeedback(m.latest) : null,
    // Anything but a literal true hides par and feedback: showing them to
    // somebody the study meant not to show them to cannot be taken back.
    shown: m.shown === true,
    practiceMinor: count(m.practiceMinor) ? Math.trunc(m.practiceMinor) : 0,
    // Par's sums go with par: drawn only while the Worker says par is shown.
    parTotals: m.shown === true ? readParTotals(m.parTotals) : null,
  };
}

/** GET /api/ai/meter, or null when signed out, switched off here, or unreachable. */
export async function fetchMeter(): Promise<MeterAnswer | null> {
  return readMeter(await get('/api/ai/meter'));
}

/** GET /api/ai/meter/:attemptId, or null. */
export async function fetchFeedback(attemptId: string): Promise<AttemptFeedback | null> {
  return readFeedback(await get(`/api/ai/meter/${encodeURIComponent(attemptId)}`));
}

/**
 * The feedback this pass earns on the clear, or null for nothing to show:
 * hidden for this learner, no closed attempt yet, a different level's
 * attempt, a pass that was not this one, or a pass by hand.
 */
export function feedbackForPass(meter: MeterAnswer | null, missionId: string, since = 0): AttemptFeedback | null {
  if (!meter?.shown || !meter.latest) return null;
  const { attempt } = meter.latest;
  if (attempt.ref !== missionId || attempt.outcome !== 'passed' || attempt.by !== 'agent') return null;
  // An agent pass of this same mission from an earlier visit is not this
  // pass: a replay by hand whose attempt has not closed yet would otherwise
  // be shown the last agent's bill.
  if (attempt.endedAt === null || attempt.endedAt < since) return null;
  return meter.latest;
}

/* ── The public figure ─────────────────────────────────────────────────── */

/** `PublicPerson.efficiency`, checked: a figure that does not read is not drawn. */
export function readSummary(raw: unknown): EfficiencySummary | null {
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as Record<string, unknown>;
  if (!count(e.passes) || e.passes < 1 || !count(e.medianRatio) || e.medianRatio <= 0) return null;
  if (typeof e.book !== 'string' || !/^[a-z0-9-]{1,32}$/.test(e.book)) return null;
  return { passes: Math.trunc(e.passes), medianRatio: e.medianRatio, book: e.book };
}

/* ── The study ─────────────────────────────────────────────────────────── */

const STAGES: readonly StudyStage[] = ['not-enrolled', 'bench1', 'learning', 'bench2', 'done', 'withdrawn'];
const BENCH_STATES: readonly BenchTaskState[] = ['todo', 'open', 'passed', 'failed', 'capped'];

export function readStudy(raw: unknown): StudyStatus | null {
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  if (!STAGES.includes(s.stage as StudyStage)) return null;
  let bench: StudyStatus['bench'] = null;
  if (s.bench && typeof s.bench === 'object') {
    const b = s.bench as Record<string, unknown>;
    if (b.form === 'A' || b.form === 'B') {
      const form = b.form as BenchForm;
      const refs: readonly string[] = BENCH_FORMS[form];
      const tasks = Array.isArray(b.tasks)
        ? b.tasks
            .map((t) => {
              if (!t || typeof t !== 'object') return null;
              const task = t as Record<string, unknown>;
              if (typeof task.ref !== 'string' || !refs.includes(task.ref)) return null;
              if (!BENCH_STATES.includes(task.state as BenchTaskState)) return null;
              return { ref: task.ref, state: task.state as BenchTaskState };
            })
            .filter(isPresent)
        : [];
      bench = { form, tasks };
    }
  }
  return {
    stage: s.stage as StudyStage,
    bench,
    secondBenchAt: stamp(s.secondBenchAt) ? s.secondBenchAt : null,
    creditMinor: count(s.creditMinor) ? Math.trunc(s.creditMinor) : 0,
  };
}

/** What a read of the study can come back as: the status, signed out, or not reachable. */
export type StudyRead = { ok: true; status: StudyStatus } | { ok: false; error: 'sign-in' | StudyRefusal | 'other' };

const REFUSALS: readonly StudyRefusal[] = ['closed', 'no-ai-key', 'no-course', 'no-ai-credits', 'enrolled', 'consent'];

export async function fetchStudy(): Promise<StudyRead> {
  return studyCall('/api/study');
}

export async function enrolStudy(): Promise<StudyRead> {
  return studyCall('/api/study/enrol', { consent: true, consentVersion: STUDY_CONSENT_VERSION });
}

export async function withdrawStudy(): Promise<StudyRead> {
  return studyCall('/api/study/withdraw', {});
}

async function studyCall(path: string, body?: unknown): Promise<StudyRead> {
  try {
    const r = await fetch(chatUrl(path).href, {
      method: body === undefined ? 'GET' : 'POST',
      credentials: 'include',
      cache: 'no-store',
      ...(body === undefined ? {} : { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }),
    });
    if (r.status === 401) return { ok: false, error: 'sign-in' };
    const data = (await r.json().catch(() => null)) as Record<string, unknown> | null;
    if (!r.ok) {
      // The enrolment's refusals each have words on the page; anything else is "try again".
      const said = data?.error as StudyRefusal;
      return { ok: false, error: REFUSALS.includes(said) ? said : 'other' };
    }
    // A withdrawal may answer with nothing in the body; it still happened.
    const status = readStudy(data) ?? (path.endsWith('/withdraw') ? withdrawnStatus() : null);
    return status ? { ok: true, status } : { ok: false, error: 'other' };
  } catch {
    return { ok: false, error: 'other' };
  }
}

function withdrawnStatus(): StudyStatus {
  return { stage: 'withdrawn', bench: null, secondBenchAt: null, creditMinor: 0 };
}

/** Which of the four benchmark kinds a task is: its place in its form, from 0. */
export function benchSlot(ref: string): number {
  for (const form of Object.values(BENCH_FORMS) as readonly (readonly string[])[]) {
    const i = form.indexOf(ref);
    if (i >= 0) return i;
  }
  return -1;
}

/* ── Telling the meter what the tab is doing ──────────────────────────── */

/**
 * A level, checkpoint or benchmark task opened in this tab, signed in: the
 * meter opens its attempt (worker/ai-meter.ts, `POST /api/ai/meter/open`),
 * so an agent lent this tab is counted to it without calling open_level.
 * Fire and forget: never awaited, never an error the learner sees (with AI
 * credits off here the route is simply 404).
 */
export function meterOpen(ref: string): void {
  void meterPost('/api/ai/meter/open', ref);
}

/**
 * A level passed Check my work in this tab: its attempt closes as passed.
 * Levels only; a checkpoint or benchmark closes on the verifier's own
 * record, and the Worker refuses one here.
 */
export function meterPass(ref: string): void {
  void meterPost('/api/ai/meter/pass', ref);
}

async function meterPost(path: string, ref: string): Promise<void> {
  try {
    await fetch(chatUrl(path).href, {
      method: 'POST',
      credentials: 'include',
      cache: 'no-store',
      keepalive: true,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ref }),
    });
  } catch {
    /* the meter missing one event is not the learner's problem */
  }
}

async function get(path: string): Promise<unknown> {
  try {
    const r = await fetch(chatUrl(path).href, { credentials: 'include', cache: 'no-store' });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

function isPresent<T>(value: T | null): value is T {
  return value !== null;
}
